import { useGetEhsLists } from "../../../hooks/useGetEhsLists";
import {
  defaultPagination,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import { useState } from "react";
import { Accordion, BaseButton } from "@velocity-ehs/component-library";
import ListItems from "./ListItems";
import classes from "./ListsAccordion.module.scss";
import { useTranslation } from "react-i18next";
import { EhsListItem } from "./EhsListsManagement";

const defaultFilters = {
  ...defaultPagination,
  rows: 20,
  totalElements: 0,
  filters: {
    search: "",
  },
  sortField: "listType",
  sortOrder: SortDirection.ASC,
};

interface Props {
  onListOpen: (e: any) => void;
  onOpenAdd: (e: any, list: any) => void;
  onOpenEdit: (item: EhsListItem) => void;
  onStatusChange: (item: EhsListItem) => void;
}

const ListsAccordion: React.FC<Props> = ({
  onListOpen,
  onOpenAdd,
  onOpenEdit,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const [lazyParams, setLazyParams] = useState(defaultFilters);
  const [activeIndices, setActiveIndices] = useState<number[]>([]);
  const { ehsLists, isFetching, pagination } = useGetEhsLists(lazyParams);

  const onPaginationChange = (event: any) => {
    setLazyParams(event);
  };

  const onTabChange = (event: any) => {
    if (!event.index) {
      setActiveIndices([]);
      onListOpen([]);
      return;
    }
    setActiveIndices(event.index);
    if (event.index.length) {
      const selectedLists = event.index.map((index: number) => {
        const { listType: name, listTypeCode: code } = ehsLists[index];
        return { name, code };
      });
      onListOpen(selectedLists);
    }
  };

  const getHeaderTemplate = (list: any, tabIndex: number) => {
    if (!activeIndices.includes(tabIndex)) return;

    // Add a handler to stop propagation of click event to prevent closing the accordion
    const handleAddClick = (e: React.MouseEvent, list: any) => {
      e.stopPropagation();
      onOpenAdd(e, list);
    };

    return (
      <div className={classes.activeHeader}>
        <span className="p-accordion-header-text">{list.listType}</span>
        <BaseButton
          label={t("universal_Add")}
          onClick={(e) => handleAddClick(e, list)}
          className="p-button-text"
        />
      </div>
    );
  };

  const formatLists = (lists: any[]) => {
    return lists.map((list, index) => {
      return {
        header: list.listType,
        headerTemplate: getHeaderTemplate(list, index),
        children: (
          <ListItems
            onOpenEdit={onOpenEdit}
            onStatusChange={onStatusChange}
            items={list.listItems}
          />
        ),
      };
    });
  };

  const onGlobalSearch = (event: any): void => {
    setLazyParams((state: any) => {
      return {
        ...state,
        filters: {
          search: event.target.value,
        },
      };
    });
  };

  return (
    <Accordion
      data={formatLists(ehsLists)}
      onGlobalSearch={onGlobalSearch}
      params={lazyParams}
      totalRecords={pagination.totalElements}
      className={classes.ehsListsAccordion}
      testId="ehs-lists"
      isLoading={isFetching}
      skeletonLength={lazyParams.rows}
      onPage={onPaginationChange}
      onTabChange={onTabChange}
    />
  );
};

export default ListsAccordion;
